import React, { useCallback, useEffect, useRef, useState } from 'react'

import { t } from 'src/translations'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'

import { useMenuSearchContext } from './MenuSearchContext'
import { SearchBox } from './SearchBox'
import useTracker from 'src/lib/js/hooks/useTracker'

type SearchInputProps = {
  placeholder?: string
  className?: string
  inputClassName?: string
  autoFocus?: boolean
  onFocus?: () => void
  onBlur?: () => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  dataTestId?: string
  ariaDescribedBy?: string
  ariaLabelledBy?: string
  inputRef?: React.RefObject<HTMLInputElement>
}

export const SearchInput = ({
  placeholder = t('search-input.placeholder.search'),
  className = 'ooSearch',
  inputClassName = 'searchBox',
  dataTestId = 'oo-search-input',
  ariaDescribedBy,
  ariaLabelledBy,
  inputRef,
  onFocus,
  onBlur,
  onKeyDown = () => {}
}: SearchInputProps) => {
  const { searchString, setSearchString, clearSearchString, setIsSearching } =
    useMenuSearchContext()
  const tracker = useTracker()
  const internalRef = useRef<HTMLInputElement>(null)
  const textEl = inputRef || internalRef

  const [searchInputValue, setSearchInputValue] = useState('')

  // Sync local input value when searchString is cleared externally
  useEffect(() => {
    if (searchString === '') {
      setSearchInputValue('')
    }
  }, [searchString])

  // Sync local input value when searchString is set via recent searches
  useEffect(() => {
    if (searchInputValue === '' && searchString !== '') {
      setSearchInputValue(searchString)
    }
  }, [searchString, searchInputValue])

  // searchString is used outside of this search box to control what items are filtered. This value gets updated with a debounce
  // searchInputValue is used internally to the search box e.g. to decide if we need to show the clear button
  // we need to make sure to update both of these values when the input changes
  const onUpdateValue = (newValue: string): void => {
    if (searchInputValue === '' && newValue !== '') {
      tracker.track(TrackingEvents.MENU_SEARCH_INITIATED, {
        searchSource: 'server'
      })
    }
    setSearchInputValue(newValue)

    if (!newValue) {
      clearSearchString()
    } else {
      setIsSearching(true)
      setSearchString(newValue)
    }
  }

  const onInputBlur = useCallback(() => {
    onBlur?.()
  }, [onBlur])

  const onInputFocus = useCallback(() => {
    setIsSearching(true)
    onFocus?.()
  }, [setIsSearching, onFocus])

  return (
    <SearchBox
      id='search-modal-input'
      value={searchInputValue}
      onValueChange={onUpdateValue}
      placeholder={placeholder}
      className={className}
      inputClassName={inputClassName}
      dataTestId={dataTestId}
      ariaLabelledBy={ariaLabelledBy}
      ariaDescribedBy={ariaDescribedBy}
      inputRef={textEl}
      onFocus={onInputFocus}
      onBlur={onInputBlur}
      onKeyDown={onKeyDown}
    />
  )
}
